import { use, useEffect, useRef } from "react";
import { TM } from "@litres-libs/tracker-manager";

import { getConfig } from "lib/get-config";
import { NavigationContext } from "lib/navigation/use-navigation";

const USER_PARAM_NONCE = "_D6W%{n";
const PERCENTAGES = [25, 50, 75, 100];

async function getCustomerUserId(id: number): Promise<string> {
  const data = new TextEncoder().encode(`${id}${USER_PARAM_NONCE}`);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const sha256 = Array.from(new Uint8Array(hashBuffer))
    .map(byte => byte.toString(16).padStart(2, "0"))
    .join("");
  return `uid_${sha256}`;
}

function pushToDataLayer(event: Record<string, unknown>) {
  const w = window as unknown as { dataLayer?: Record<string, unknown>[] };
  w.dataLayer ??= [];
  w.dataLayer.push(event);
}

export function useFragmentAnalytics() {
  const { art, user, trials } = getConfig();
  const { page, totalPages } = use(NavigationContext)!;

  const isFragment = trials === 1;

  const stateRef = useRef({
    hasStarted: false,
    hasFinished: false,
    checkpoints: [] as number[],
    totalPages: 0,
  });

  useEffect(() => {
    if (!isFragment || page === null || totalPages === 0) {
      return;
    }

    const state = stateRef.current;

    if (state.totalPages !== totalPages) {
      state.totalPages = totalPages;
      state.checkpoints = PERCENTAGES.map(p =>
        Math.ceil((p / 100) * totalPages)
      );
    }

    if (!state.hasStarted) {
      state.hasStarted = true;
      getCustomerUserId(user).then(customerUserId => {
        pushToDataLayer({
          event: "start_read_fragment",
          content_id: art,
          customer_user_id: customerUserId,
        });
      });
    }

    let index = state.checkpoints.indexOf(page);
    if (index === -1) {
      index = state.checkpoints.indexOf(page - 1);
    }
    if (index !== -1) {
      const percent = PERCENTAGES[index];
      state.checkpoints[index] = -1;
      getCustomerUserId(user).then(customerUserId => {
        pushToDataLayer({
          event: "percentage_of_read",
          content_id: art,
          percent,
          customer_user_id: customerUserId,
        });
      });
    }

    if (totalPages - page <= 1 && !state.hasFinished) {
      state.hasFinished = true;
      getCustomerUserId(user).then(customerUserId => {
        pushToDataLayer({
          event: "end_read_fragment",
          content_id: art,
          customer_user_id: customerUserId,
        });
      });
      TM.sendEvent(["LITRES_TRACKER"], {
        eventType: "read_fragment_end",
        params: { id: art, user_id: user },
      });
    }
  }, [isFragment, page, totalPages, user, art]);
}
